import { api } from './api';
import {
  normalizeSignatureRequestStatus,
  normalizeSignatureRule,
  normalizeSignatureStatus,
  normalizeSignatureType,
  SignatureRequestStatusCode,
  SignatureRuleCode,
  SignatureStatusCode,
  SignatureTypeCode,
} from '../utils/signatureStatusUtils';

export {
  SignatureRequestStatusCode,
  SignatureStatusCode,
  SignatureTypeCode,
  SignatureRuleCode,
};

export interface CreateSignatureRequestPayload {
  documentId: string;
  documentVersionId?: string | null;
  signatureRule: number;
  minSignatures: number;
  expiresAt?: string | null;
  signers: SignerInputDto[];
}

export interface DocumentSignerDto {
  id: string;
  signatureRequestId: string;
  documentWorkflowStepApproverId?: string | null;
  userId?: string | null;
  name: string;
  email: string;
  signatureType: number;
  isRequired: boolean;
  status: number;
  signedAt?: string | null;
  rejectedAt?: string | null;
  rejectionReason?: string | null;
  signatureOrder: number;
  tokenExpiresAt?: string | null;
  createdAt: string;
  updatedAt: string;
  placementPage?: number | null;
  placementX?: number | null;
  placementY?: number | null;
  placementWidth?: number | null;
  placementHeight?: number | null;
}

export interface DocumentSignatureDto {
  id: string;
  documentId: string;
  documentVersionId?: string | null;
  customerId: string;
  status: number;
  signatureRule: number;
  minSignatures: number;
  expiresAt?: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string | null;
  /** PDF consolidado com assinaturas já gerado no storage. */
  finalSignedDocumentAvailable?: boolean;
  /** Solicitação concluída; PDF consolidado ainda em fila ou processamento. */
  finalSignedDocumentProcessing?: boolean;
  /** Solicitação concluída, mas a geração do PDF consolidado falhou. */
  finalSignedDocumentFailed?: boolean;
  /** PDF parcial com assinaturas já coletadas (enquanto a solicitação está em andamento). */
  partialSignedDocumentAvailable?: boolean;
  /** Já há assinaturas, mas a geração/atualização da prévia PDF falhou. */
  partialSignedDocumentFailed?: boolean;
  /** Momento da última atualização do PDF parcial no storage. */
  partialDocumentUpdatedAt?: string | null;
  /** Quantidade de signatários que já assinaram. */
  signedCount?: number;
  /** Total de signatários (required + optional). */
  totalSigners?: number;
  /** ID público do QR / validador (não é o IP da evidência). */
  publicId?: string | null;
  /** URL pública de validação correspondente ao publicId. */
  validationUrl?: string | null;
  signers: DocumentSignerDto[];
}

export interface SignatureEvidenceDto {
  id: string;
  documentSignerId: string;
  ipAddress: string;
  userAgent: string;
  deviceInfo?: string | null;
  documentHash: string;
  signatureType: number;
  certificateSerial?: string | null;
  certificateIssuer?: string | null;
  certificateSubject?: string | null;
  handwrittenImageKey?: string | null;
  cryptographicSignatureKey?: string | null;
  timestamp: string;
  createdAt: string;
}

export interface SignaturePlacementInputDto {
  page: number;
  x: number;
  y: number;
  width?: number;
  height?: number;
}

export interface SignerInputDto {
  documentWorkflowStepApproverId?: string | null;
  name: string;
  email: string;
  signatureType: number;
  isRequired?: boolean;
  placement?: SignaturePlacementInputDto | null;
}

export type SignatureValidationKind = 'icp_brasil' | 'electronic' | 'handwritten';

export interface SignatureValidationSignerDto {
  name: string;
  email: string;
  signatureType: number;
  signatureKind: SignatureValidationKind;
  status: number;
  signedAt?: string | null;
  documentHash?: string | null;
}

export type SignatureIntegrityStatus =
  | 'match_original'
  | 'match_final'
  | 'mismatch'
  | 'unknown';

export interface PadesSignatureVerificationDto {
  signatureFieldName: string;
  signerName?: string | null;
  certificateSubject?: string | null;
  signingTime?: string | null;
  signatureIntact: boolean;
  chainValid: boolean;
  errors: string[];
}

export interface SignatureValidationResultDto {
  publicId: string;
  signatureRequestId: string;
  documentId: string;
  documentTitle: string;
  originalBinarySha256: string;
  finalDocumentSha256?: string | null;
  status: number;
  completedAt?: string | null;
  validationUrl: string;
  barukSealApplied: boolean;
  signers: SignatureValidationSignerDto[];
  uploadedSha256?: string | null;
  matchesOriginal?: boolean | null;
  matchesFinal?: boolean | null;
  integrityStatus?: SignatureIntegrityStatus | null;
  embeddedSignatures?: PadesSignatureVerificationDto[] | null;
}

function normalizeValidationResult(data: SignatureValidationResultDto): SignatureValidationResultDto {
  return {
    ...data,
    status: normalizeSignatureRequestStatus(data.status),
    signers: (data.signers ?? []).map((signer) => ({
      ...signer,
      status: normalizeSignatureStatus(signer.status),
      signatureType: normalizeSignatureType(signer.signatureType),
    })),
  };
}

export const SIGNATURE_VALIDATION_MAX_UPLOAD_BYTES = 52_428_800;

function unwrapAxiosMessage(err: unknown): string {
  const e = err as { response?: { data?: { message?: string } }; message?: string };
  const rawMessage = e?.response?.data?.message || e?.message || 'Erro na requisição';
  return rawMessage;
}

function normalizeSignerDto(signer: DocumentSignerDto): DocumentSignerDto {
  return {
    ...signer,
    status: normalizeSignatureStatus(signer.status),
    signatureType: normalizeSignatureType(signer.signatureType),
  };
}

function normalizeSignatureDto(signature: DocumentSignatureDto): DocumentSignatureDto {
  return {
    ...signature,
    status: normalizeSignatureRequestStatus(signature.status),
    signatureRule: normalizeSignatureRule(signature.signatureRule),
    signers: (signature.signers ?? []).map(normalizeSignerDto),
  };
}

class DocumentSignatureService {
  private resolveApiRootUrl(): string {
    const raw = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/$/, '');
    if (raw.endsWith('/api')) {
      return raw;
    }
    return `${raw}/api`;
  }

  private getAuthToken(): string | null {
    return sessionStorage.getItem('@KnowInDoc:accessToken');
  }

  private buildAuthHeaders(): HeadersInit {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error('Token de autenticação não encontrado');
    }
    return {
      Authorization: `Bearer ${token}`,
      tenant_id: sessionStorage.getItem('@KnowInDoc:customerToken') || '',
      customer_id: sessionStorage.getItem('@KnowInDoc:customer_id') || '',
      user_id: sessionStorage.getItem('@KnowInDoc:user_id') || '',
    };
  }

  private sanitizeFileName(fileName: string): string {
    return fileName
      .replace(/[<>:"/\\|?*]/g, '_')
      .replace(/\s+/g, '_')
      .substring(0, 255);
  }

  private parseFileNameFromContentDisposition(
    contentDisposition: string | null,
    fallback: string
  ): string {
    if (!contentDisposition) {
      return fallback;
    }
    const utf8Match = /filename\*=UTF-8''([^;\s]+)/i.exec(contentDisposition);
    if (utf8Match?.[1]) {
      try {
        return decodeURIComponent(utf8Match[1].trim());
      } catch {
      }
    }
    const quotedMatch = /filename="([^"]+)"/i.exec(contentDisposition);
    if (quotedMatch?.[1]) {
      return quotedMatch[1];
    }
    const plainMatch = /filename=([^;\s]+)/i.exec(contentDisposition);
    if (plainMatch?.[1]) {
      return plainMatch[1].replace(/"/g, '');
    }
    return fallback;
  }

  private triggerBrowserDownload(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.style.display = 'none';
    link.href = url;
    link.download = this.sanitizeFileName(fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 100);
  }

  private async fetchSignedFinalPdfResponse(signatureRequestId: string): Promise<Response> {
    const response = await fetch(
      `${this.resolveApiRootUrl()}/signatures/${signatureRequestId}/download`,
      {
        method: 'GET',
        headers: this.buildAuthHeaders(),
      }
    );

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('404: PDF assinado ainda não disponível ou solicitação não encontrada.');
      }
      if (response.status === 401) {
        throw new Error('401: Não autorizado. Faça login novamente.');
      }
      if (response.status === 403) {
        throw new Error('403: Acesso negado a este documento.');
      }
      throw new Error(`Erro ao obter PDF assinado (${response.status})`);
    }

    return response;
  }

  async fetchSignedFinalPdfBlob(signatureRequestId: string): Promise<Blob> {
    const response = await this.fetchSignedFinalPdfResponse(signatureRequestId);
    return response.blob();
  }

  async downloadSignedFinalPdf(signatureRequestId: string, suggestedFileName?: string): Promise<void> {
    const fallbackName = suggestedFileName || 'documento-assinado.pdf';
    const response = await this.fetchSignedFinalPdfResponse(signatureRequestId);
    const blob = await response.blob();
    const headerName = response.headers.get('Content-Disposition');
    const fileName = this.parseFileNameFromContentDisposition(headerName, fallbackName);
    this.triggerBrowserDownload(blob, fileName);
  }

  private async fetchPartialSignedPdfResponse(signatureRequestId: string): Promise<Response> {
    const response = await fetch(
      `${this.resolveApiRootUrl()}/signatures/${signatureRequestId}/partial-download`,
      {
        method: 'GET',
        headers: this.buildAuthHeaders(),
      }
    );

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('404: PDF parcial ainda não disponível ou solicitação não encontrada.');
      }
      if (response.status === 401) {
        throw new Error('401: Não autorizado. Faça login novamente.');
      }
      if (response.status === 403) {
        throw new Error('403: Acesso negado a este documento.');
      }
      throw new Error(`Erro ao obter PDF parcial (${response.status})`);
    }

    return response;
  }

  async downloadPartial(signatureRequestId: string, suggestedFileName?: string): Promise<void> {
    const fallbackName = suggestedFileName || 'documento-parcial-assinado.pdf';
    const response = await this.fetchPartialSignedPdfResponse(signatureRequestId);
    const blob = await response.blob();
    const headerName = response.headers.get('Content-Disposition');
    const fileName = this.parseFileNameFromContentDisposition(headerName, fallbackName);
    this.triggerBrowserDownload(blob, fileName);
  }

  /**
   * Busca blob do PDF parcial (ou final se concluído) para exibição no viewer.
   */
  async fetchPartialSignedPdfBlob(signatureRequestId: string): Promise<Blob> {
    const response = await this.fetchPartialSignedPdfResponse(signatureRequestId);
    return response.blob();
  }

  async retryFinalPdf(signatureRequestId: string): Promise<DocumentSignatureDto> {
    try {
      const { data } = await api.post<DocumentSignatureDto>(
        `/signatures/${signatureRequestId}/retry-final-pdf`
      );
      return normalizeSignatureDto(data);
    } catch (err) {
      throw new Error(unwrapAxiosMessage(err));
    }
  }

  async retryPartialPdf(signatureRequestId: string): Promise<DocumentSignatureDto> {
    try {
      const { data } = await api.post<DocumentSignatureDto>(
        `/signatures/${signatureRequestId}/retry-partial-pdf`
      );
      return normalizeSignatureDto(data);
    } catch (err) {
      throw new Error(unwrapAxiosMessage(err));
    }
  }

  async getActiveByDocument(documentId: string): Promise<DocumentSignatureDto | null> {
    try {
      const { data } = await api.get<DocumentSignatureDto>(`/signatures/document/${documentId}`);
      return normalizeSignatureDto(data);
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        return null;
      }
      throw new Error(unwrapAxiosMessage(err));
    }
  }

  async getLatestByDocument(documentId: string): Promise<DocumentSignatureDto | null> {
    try {
      const { data } = await api.get<DocumentSignatureDto>(`/signatures/document/${documentId}/latest`);
      return normalizeSignatureDto(data);
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        return null;
      }
      throw new Error(unwrapAxiosMessage(err));
    }
  }

  async addSigner(signatureRequestId: string, signer: SignerInputDto): Promise<DocumentSignerDto> {
    try {
      const { data } = await api.post<DocumentSignerDto>(`/signatures/${signatureRequestId}/signers`, signer);
      return normalizeSignerDto(data);
    } catch (err) {
      throw new Error(unwrapAxiosMessage(err));
    }
  }

  async removeSigner(signatureRequestId: string, signerId: string): Promise<void> {
    try {
      await api.delete(`/signatures/${signatureRequestId}/signers/${signerId}`);
    } catch (err) {
      throw new Error(unwrapAxiosMessage(err));
    }
  }

  async sendForSignature(signatureRequestId: string): Promise<DocumentSignatureDto> {
    try {
      const { data } = await api.post<DocumentSignatureDto>(`/signatures/${signatureRequestId}/send`);
      return normalizeSignatureDto(data);
    } catch (err) {
      throw new Error(unwrapAxiosMessage(err));
    }
  }

  async getEvidence(signatureRequestId: string): Promise<SignatureEvidenceDto[]> {
    try {
      const { data } = await api.get<SignatureEvidenceDto[]>(`/signatures/${signatureRequestId}/evidence`);
      return data ?? [];
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        return [];
      }
      throw new Error(unwrapAxiosMessage(err));
    }
  }

  async getEvidenceBySigner(signerId: string): Promise<SignatureEvidenceDto[]> {
    try {
      const { data } = await api.get<SignatureEvidenceDto[]>(`/signatures/signers/${signerId}/evidence`);
      return data ?? [];
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        return [];
      }
      throw new Error(unwrapAxiosMessage(err));
    }
  }

  async getValidationByPublicId(publicId: string): Promise<SignatureValidationResultDto | null> {
    try {
      const url = `${this.resolveApiRootUrl()}/v1/signature/validate/${encodeURIComponent(publicId)}`;
      const response = await fetch(url);
      if (response.status === 404) {
        return null;
      }
      if (!response.ok) {
        throw new Error('Não foi possível validar o documento.');
      }
      return normalizeValidationResult((await response.json()) as SignatureValidationResultDto);
    } catch (err) {
      throw new Error(unwrapAxiosMessage(err));
    }
  }

  async getValidationByHash(sha256: string): Promise<SignatureValidationResultDto | null> {
    try {
      const normalized = sha256.trim().toLowerCase();
      const url = `${this.resolveApiRootUrl()}/v1/signature/validate/by-hash/${encodeURIComponent(normalized)}`;
      const response = await fetch(url);
      if (response.status === 404) {
        return null;
      }
      if (!response.ok) {
        throw new Error('Não foi possível validar o documento pelo hash.');
      }
      return normalizeValidationResult((await response.json()) as SignatureValidationResultDto);
    } catch (err) {
      throw new Error(unwrapAxiosMessage(err));
    }
  }

  async verifyUpload(file: File, publicId?: string): Promise<SignatureValidationResultDto> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const trimmedPublicId = publicId?.trim();
      if (trimmedPublicId) {
        formData.append('publicId', trimmedPublicId);
      }

      const url = `${this.resolveApiRootUrl()}/v1/signature/validate/verify-upload`;
      const response = await fetch(url, {
        method: 'POST',
        body: formData,
      });

      if (response.status === 404) {
        throw new Error('Nenhum registro encontrado para o ID público informado.');
      }
      if (response.status === 413) {
        throw new Error('O arquivo excede o tamanho máximo permitido (50 MB).');
      }
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message || 'Não foi possível verificar o documento enviado.');
      }

      return normalizeValidationResult((await response.json()) as SignatureValidationResultDto);
    } catch (err) {
      throw new Error(unwrapAxiosMessage(err));
    }
  }

  async createSignatureRequest(payload: CreateSignatureRequestPayload): Promise<DocumentSignatureDto> {
    try {
      const { data } = await api.post<DocumentSignatureDto>('/signatures', {
        documentId: payload.documentId,
        documentVersionId: payload.documentVersionId ?? null,
        signatureRule: payload.signatureRule,
        minSignatures: payload.minSignatures,
        expiresAt: payload.expiresAt ?? null,
        signers: payload.signers,
      });
      return normalizeSignatureDto(data);
    } catch (err) {
      throw new Error(unwrapAxiosMessage(err));
    }
  }

  async getSignatureRequest(signatureRequestId: string): Promise<DocumentSignatureDto> {
    try {
      const { data } = await api.get<DocumentSignatureDto>(`/signatures/${signatureRequestId}`);
      return normalizeSignatureDto(data);
    } catch (err) {
      throw new Error(unwrapAxiosMessage(err));
    }
  }

  async updateSignersPlacements(
    signatureRequestId: string,
    placements: Record<string, SignaturePlacementInputDto | null>
  ): Promise<void> {
    try {
      await api.put(`/signatures/${signatureRequestId}/signers/placements`, {
        signerPlacements: placements,
      });
    } catch (err) {
      throw new Error(unwrapAxiosMessage(err));
    }
  }

  async sendSignatureRequest(signatureRequestId: string): Promise<DocumentSignatureDto> {
    try {
      const { data } = await api.post<DocumentSignatureDto>(`/signatures/${signatureRequestId}/send`);
      return normalizeSignatureDto(data);
    } catch (err) {
      throw new Error(unwrapAxiosMessage(err));
    }
  }
}

export const documentSignatureService = new DocumentSignatureService();
